// The SPA's client for the server-side session store (PRD 011 §6.1 — the server
// is the system of record). The hosted SessionDO holds each session's rich
// snapshot (the PersistedSession blob) keyed by sessionId; the SPA mirrors to it
// on the persist debounce, lists from it for the sidebar, and hydrates from it on
// open — falling back to the IndexedDB cache when the backend is unreachable
// (offline, or a local gateway without these endpoints). All calls are
// best-effort and never throw: a null/no-op result means "use the local cache."

import { resolveBridgeEndpoint } from '@/lib/bridge/config'
import { loadKeys } from '@/lib/keys/keyStore'
import type { SessionMeta } from '@/lib/bridge/protocol'
import type { PersistedSession } from './sessionPersistence'

async function endpoint(): Promise<{ base: string; token: string } | null> {
  const keys = await loadKeys()
  const token = keys?.bridgeToken
  if (!token) return null
  return { base: resolveBridgeEndpoint(keys?.bridgeUrl), token }
}

function headers(token: string): HeadersInit {
  return { 'content-type': 'application/json', 'x-bridge-token': token }
}

/** List the user's sessions from the backend, or null if unreachable/unpaired. */
export async function remoteListSessions(): Promise<SessionMeta[] | null> {
  const e = await endpoint()
  if (!e) return null
  try {
    const res = await fetch(`${e.base}/bridge/sessions`, { headers: headers(e.token) })
    if (!res.ok) return null
    const data = (await res.json()) as { sessions?: SessionMeta[] }
    return data.sessions ?? []
  } catch {
    return null
  }
}

/** Hydrate a session's rich snapshot from the backend, or null if absent. */
export async function remoteLoadSession(id: string): Promise<PersistedSession | null> {
  const e = await endpoint()
  if (!e) return null
  try {
    const res = await fetch(`${e.base}/bridge/sessions/${encodeURIComponent(id)}/snapshot`, {
      headers: headers(e.token)
    })
    if (!res.ok) return null
    const data = (await res.json()) as { blob?: string | null }
    if (!data.blob) return null
    return JSON.parse(data.blob) as PersistedSession
  } catch {
    return null
  }
}

/** Mirror a session snapshot to the backend (best-effort write-through). The
 *  IndexedDB copy stays the local cache; this makes the server authoritative so
 *  the session survives a cache clear / new browser / new device. */
export async function remotePutSession(rec: PersistedSession): Promise<void> {
  const e = await endpoint()
  if (!e) return
  try {
    await fetch(`${e.base}/bridge/sessions/${encodeURIComponent(rec.id)}/snapshot`, {
      method: 'PUT',
      headers: headers(e.token),
      body: JSON.stringify(rec)
    })
  } catch {
    // best-effort; IndexedDB still holds the local copy until the next sync
  }
}

/** Delete a session on the backend (best-effort) — the user-delete path. */
export async function remoteDeleteSession(id: string): Promise<void> {
  const e = await endpoint()
  if (!e) return
  try {
    await fetch(`${e.base}/bridge/sessions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: headers(e.token)
    })
  } catch {
    // best-effort
  }
}
